import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { generateChecklist, perfilParaMotor, type ChecklistItem } from '../lib/compliance-engine';
import { ITEMS_AUTOMATICOS, REGLA_COMUN_AUTOMATICOS, esAutomatico, puedeVerChecklist, textoNoAplicaManual } from '../lib/checklist-reglas';
import { ITEMS_SP } from '../lib/servicios-permanentes';
import { textoVigenciaSugerida, versionesAnteriores, type DocumentoItem } from '../lib/item-anexo';
import { SSO_KIND_LABELS, tamanoLegible, esTablaInexistente, hoyISO, type SSOKindArchivo } from '../lib/sso-documentos';
import { codigoLegal, type NavItem } from '../lib/menu';
import { fechaCortaLocal } from '../lib/fechas';
import MesesGA07 from '../components/MesesGA07';
import AniosGA11 from '../components/AniosGA11';
import EnlaceArchivo from '../components/EnlaceArchivo';
import { abrirArchivo } from '../lib/archivos';
import type { InformeGA07 } from '../lib/ga07-meses';
import './ItemAnexoPage.css';

// Página común de un ítem del Anexo 1 (bloque 7, B y C; pedido de Kevin del
// 20/10): GA-01 a GA-12 y SP-01 a SP-09. Todo se sigue guardando en Documentos
// SSO (repositorio único): esta página solo lee y lleva a Subir con el ítem
// elegido. El estado se cambia en el Checklist (los automáticos, en su módulo).

type Documento = DocumentoItem;

interface Archivo {
  id: string;
  document_id: string;
  file_path: string;
  file_name: string;
  file_size_kb: number | null;
  kind: SSOKindArchivo;
}

interface EstadoGuardado {
  estado: string;
  auto_source: string | null;
  no_aplica_justificacion: string | null;
  no_aplica_por: string | null;
  no_aplica_at: string | null;
}

const ESTADO_LABELS: Record<string, string> = {
  pendiente: 'Pendiente', en_proceso: 'En proceso', cumple: 'Cumple', no_cumple: 'No cumple', no_aplica: 'No aplica',
};

// Fecha sin hora, tal cual; un instante (uploaded_at), en el día de Ecuador (lib/fechas).
function fecha(iso: string | null): string {
  return fechaCortaLocal(iso);
}

export default function ItemAnexoPage({ itemId, onNavigate }: {
  itemId: string;
  onNavigate: (tab: NavItem) => void;
}) {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const companyId = (impersonated ?? company)?.id ?? '';
  // Igual que Documentos SSO: suben admin, manager y el superadmin en vista de empresa.
  const puedeSubir = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  // Bloque 9 (M19): el Checklist no es para el viewer.
  const veChecklist = puedeVerChecklist(profile?.role, !!impersonated);

  const [item, setItem] = useState<ChecklistItem | null>(null);
  const [estado, setEstado] = useState<EstadoGuardado | null>(null);
  const [docs, setDocs] = useState<Documento[]>([]);
  const [archivos, setArchivos] = useState<Archivo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true);
    setError('');
    const [perfil, est, documentos] = await Promise.all([
      supabase.from('company_sso_profile').select('*').eq('company_id', companyId).maybeSingle(),
      supabase.from('checklist_item_estados')
        .select('estado, auto_source, no_aplica_justificacion, no_aplica_por, no_aplica_at')
        .eq('company_id', companyId).eq('item_id', itemId).maybeSingle(),
      supabase.from('sso_documents').select('*')
        .eq('company_id', companyId).eq('checklist_item_id', itemId)
        .order('created_at', { ascending: false }),
    ]);
    if (perfil.error) console.error('Error al leer el Perfil SSO:', perfil.error.message);
    if (est.error) console.error(`Error al leer el estado de ${itemId}:`, est.error.message);
    if (documentos.error) {
      console.error(`Error al leer los documentos de ${itemId}:`, documentos.error.message);
      setError('No se pudieron cargar los documentos del ítem. Intenta de nuevo.');
    }
    const encontrado = generateChecklist(perfilParaMotor(perfil.error ? null : perfil.data)).items.find(i => i.id === itemId) ?? null;
    const lista = (documentos.data ?? []) as Documento[];
    setItem(encontrado);
    setEstado((est.data as EstadoGuardado | null) ?? null);
    setDocs(lista);
    if (lista.length > 0) {
      const { data: arch, error: archErr } = await supabase.from('sso_document_files')
        .select('id, document_id, file_path, file_name, file_size_kb, kind')
        .eq('company_id', companyId).in('document_id', lista.map(d => d.id));
      if (archErr && !esTablaInexistente(archErr)) console.error('Error al leer los archivos del ítem:', archErr.message);
      setArchivos((arch ?? []) as Archivo[]);
    } else {
      setArchivos([]);
    }
    setCargando(false);
  }, [companyId, itemId]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = (aprobadoDesde?: string) => {
    sessionStorage.setItem('ss_init_checklist_item', itemId);
    sessionStorage.setItem('ss_init_subir', '1');
    if (aprobadoDesde) sessionStorage.setItem('ss_init_aprobado_desde', aprobadoDesde);
    onNavigate('documentos-sso');
  };
  const verEnDocumentos = () => {
    sessionStorage.setItem('ss_init_checklist_item', itemId);
    onNavigate('documentos-sso');
  };
  const verEnChecklist = () => {
    sessionStorage.setItem('ss_init_checklist_abrir_item', itemId);
    onNavigate('checklist');
  };

  if (cargando) return <div className="ia-cargando">Cargando el ítem…</div>;
  if (!item) return <div className="ia-cargando">No existe el ítem {itemId} en el Checklist.</div>;

  const definicionSP = ITEMS_SP.find(s => s.id === itemId);
  const vigentes = docs.filter(d => d.is_current);
  const enCadena = new Set(vigentes.flatMap(d => versionesAnteriores(d, docs).map(v => v.id)));
  const sueltas = docs.filter(d => !d.is_current && !enCadena.has(d.id));
  const informes: InformeGA07[] = docs.map(d => ({ id: d.id, file_url: d.file_url, approved_from: d.approved_from, is_current: d.is_current, created_at: d.created_at }));
  const valorEstado = estado?.estado ?? 'pendiente';
  const automatico = esAutomatico(itemId);
  const hoy = hoyISO();

  const adicionales = (docId: string) => archivos.filter(a => a.document_id === docId && a.kind !== 'principal');

  return (
    <div className="ia-pagina">
      <header className="ia-cabecera">
        <div className="ia-codigos">
          <span className="ia-codigo">{codigoLegal(item)}</span>
          <span className="ia-id" title="Id interno del ítem en StorSafe">{item.id}</span>
        </div>
        <h1 className="ia-titulo">{item.titulo}</h1>
        <p className="ia-descripcion">{item.descripcion}</p>
        {item.normativa.length > 0 && (
          <div className="ia-normativa" aria-label="Normativa">
            {item.normativa.map(n => <span key={n} className="ia-badge">{n}</span>)}
          </div>
        )}
        {definicionSP?.condicion && (
          <p className="ia-condicion"><strong>Condición de aplicación:</strong> {definicionSP.condicion}</p>
        )}
        {definicionSP && (
          <p className="ia-fuente">Anexo 1, Servicios Permanentes, ítem {definicionSP.numero_legal}.</p>
        )}
      </header>

      <section className="ia-tarjeta" aria-labelledby="ia-estado-titulo">
        <h2 id="ia-estado-titulo">Estado en el Checklist</h2>
        {!item.aplica ? (
          <div className="ia-na">
            <p>{item.sin_calcular ? item.razon_no_aplica : `No aplica a tu empresa — ${item.razon_no_aplica ?? ''}`}</p>
            {item.sin_calcular && <p className="ia-nota">Mientras esté sin calcular, no cuenta en el cumplimiento.</p>}
            <button type="button" className="ia-btn-secundario" onClick={() => onNavigate('sso-perfil')}>Ir al Perfil SSO</button>
          </div>
        ) : (
          <>
            <div className="ia-estado-fila">
              <span className={`ia-estado ia-estado-${valorEstado}`}>{ESTADO_LABELS[valorEstado] ?? valorEstado}</span>
              {automatico && <span className="ia-badge" title="Este ítem lo marca su módulo y no admite edición manual">🔒 Automático</span>}
              <span className="ia-peso">{item.peso.toFixed(1)}% del cumplimiento</span>
            </div>
            {automatico && (
              <p className="ia-nota">Lo decide {ITEMS_AUTOMATICOS[itemId].modulo}: {ITEMS_AUTOMATICOS[itemId].regla}. {REGLA_COMUN_AUTOMATICOS}</p>
            )}
            {valorEstado === 'no_aplica' && estado && (
              <p className="ia-nota">{textoNoAplicaManual(null, estado.no_aplica_at, estado.no_aplica_justificacion ?? '')}</p>
            )}
            {definicionSP && !automatico && (
              <p className="ia-nota">Estado manual por ahora; la evidencia en Documentos SSO es opcional. Cuando se definan las inspecciones con foto, este ítem se cumplirá con una inspección.</p>
            )}
            {veChecklist && (
              <button type="button" className="ia-btn-secundario" onClick={verEnChecklist}>
                {automatico ? 'Ver en el Checklist' : 'Cambiar el estado en el Checklist'}
              </button>
            )}
          </>
        )}
      </section>

      <section className="ia-tarjeta" aria-labelledby="ia-docs-titulo">
        <div className="ia-docs-cabecera">
          <h2 id="ia-docs-titulo">Documentos del ítem</h2>
          <div className="ia-acciones">
            <button type="button" className="ia-btn-secundario" onClick={verEnDocumentos}>Ver en Documentos SSO</button>
            {puedeSubir && <button type="button" className="ia-btn-primario" onClick={() => subir()}>Subir documento</button>}
          </div>
        </div>
        <p className="ia-nota">{textoVigenciaSugerida(itemId)}</p>
        {!puedeSubir && <p className="ia-nota">Solo el administrador o el gerente pueden subir documentos.</p>}

        {itemId === 'GA-07' && (
          <MesesGA07 informes={informes} onAbrir={inf => void abrirArchivo('sso-documents', inf.file_url)}
            onSubir={puedeSubir ? subir : undefined} hoy={hoy} />
        )}
        {itemId === 'GA-11' && (
          <AniosGA11 informes={informes} onAbrir={inf => void abrirArchivo('sso-documents', inf.file_url)}
            onSubir={puedeSubir ? subir : undefined} hoy={hoy} />
        )}

        {error && <p role="alert" className="ia-error">{error}</p>}
        {!error && docs.length === 0 && <p className="ia-vacio">Todavía no hay documentos de este ítem.</p>}

        <ul className="ia-docs">
          {vigentes.map(d => {
            const anteriores = versionesAnteriores(d, docs);
            const vencido = !!d.expiry_date && d.expiry_date < hoy;
            return (
              <li key={d.id} className="ia-doc">
                <div className="ia-doc-fila">
                  <EnlaceArchivo bucket="sso-documents" valor={d.file_url} className="ia-doc-titulo">{d.title}</EnlaceArchivo>
                  <span className="ia-badge ia-badge-vigente">Vigente · v{d.version}</span>
                  {vencido && <span className="ia-badge ia-badge-vencido">Vencido</span>}
                </div>
                <p className="ia-doc-meta">
                  Aprobado desde {fecha(d.approved_from)} · Vigente hasta {d.expiry_date ? fecha(d.expiry_date) : 'sin fecha'} · {d.file_name} ({tamanoLegible(d.file_size_kb)})
                </p>
                {adicionales(d.id).length > 0 && (
                  <ul className="ia-adicionales" aria-label="Archivos adicionales">
                    {adicionales(d.id).map(a => (
                      <li key={a.id}>
                        <EnlaceArchivo bucket="sso-documents" valor={a.file_path}>{SSO_KIND_LABELS[a.kind] ?? a.kind}: {a.file_name}</EnlaceArchivo> ({tamanoLegible(a.file_size_kb)})
                      </li>
                    ))}
                  </ul>
                )}
                {anteriores.length > 0 && (
                  <details className="ia-versiones">
                    <summary>{anteriores.length} {anteriores.length === 1 ? 'versión anterior' : 'versiones anteriores'}</summary>
                    <ul>
                      {anteriores.map(v => (
                        <li key={v.id}>
                          <EnlaceArchivo bucket="sso-documents" valor={v.file_url}>v{v.version} — {v.title}</EnlaceArchivo> · aprobado desde {fecha(v.approved_from)} · cargado el {fecha(v.created_at)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
          {sueltas.map(d => (
            <li key={d.id} className="ia-doc ia-doc-archivado">
              <div className="ia-doc-fila">
                <EnlaceArchivo bucket="sso-documents" valor={d.file_url} className="ia-doc-titulo">{d.title}</EnlaceArchivo>
                <span className="ia-badge">Archivado · v{d.version}</span>
              </div>
              <p className="ia-doc-meta">Aprobado desde {fecha(d.approved_from)} · cargado el {fecha(d.created_at)}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
